import React from "react";

import { getYAxisWidth } from "@/helpers/chartHelper";

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  ResponsiveContainerProps,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import { YAxisTick } from "./HorizontalGroupedBarChart";
import { domainTickCalculate } from "./utils";

interface HorizontalBarChartProps
  extends Omit<ResponsiveContainerProps, "children"> {
  data: {
    name: string;
    value: string;
  }[];
  title?: string;
  onBarClick?: (name: string) => void;
}

const HorizontalBarChart = React.forwardRef<
  HTMLDivElement,
  HorizontalBarChartProps
>((props, ref) => {
  const { data, title, onBarClick, ...rest } = props;

  const numericValues = data
    .map((item) => Number(item.value))
    .filter((v) => !Number.isNaN(v));
  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = React.useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  const maxLabelWidth = getYAxisWidth(data, "12px", 12);

  const yAxisWidth = Math.ceil(maxLabelWidth) + 36;

  return (
    <ResponsiveContainer
      ref={ref}
      {...rest}
      width="100%"
      height={rest.height || 600}
    >
      <BarChart
        data={data}
        layout="vertical"
        barCategoryGap="15%"
        margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
        aria-label={title}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          domain={[0, axisDomainMax * 1.2]}
          ticks={axisTicks}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          fontSize={12}
        />
        <YAxis
          dataKey="name"
          type="category"
          className="text-[#000000]/70"
          tickMargin={10}
          width={yAxisWidth}
          tick={YAxisTick}
        />
        <Tooltip />
        <Bar
          dataKey="value"
          fill="#088C7A"
          className="font-semibold text-[#000000]/70"
          maxBarSize={31}
          radius={[0, 999, 999, 0]}
          onClick={(data) => onBarClick?.(data.name)}
        >
          <LabelList
            dataKey="value"
            position="right"
            fill="#000000B2"
            fontWeight={700}
            fontSize={12}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
});

HorizontalBarChart.displayName = "HorizontalBarChart";

export default HorizontalBarChart;
